import {
  DEFAULT_TABLE_ROWS_PER_PAGE,
  type RowsPerPageOption,
  useDataTable,
} from '@ovComponents/2-component/dataTable';
import type { Theme } from 'hephaestus/core';
import type { TFunction } from 'i18next';
import { useEffect, useMemo } from 'react';
import { buildFlagGlossaryColumns } from '../table/flagGlossaryGrid.columns';
import { FLAG_GLOSSARY_PAGE_SIZE } from './flagGlossary.config';
import type {
  FlagGlossaryFilterValues,
  FlagGlossaryRowView,
} from './flagGlossary.types';

type UseFlagGlossaryGridInput = {
  theme: Theme;
  t: TFunction;
  rows: FlagGlossaryRowView[];
  filters: FlagGlossaryFilterValues;
  loading: boolean;
};

export const useFlagGlossaryGrid = ({
  theme,
  t,
  rows,
  filters,
  loading,
}: UseFlagGlossaryGridInput) => {
  const columns = useMemo(() => buildFlagGlossaryColumns(t, theme), [t, theme]);

  const { table, pagination } = useDataTable({
    data: rows,
    columns,
    sortingMode: 'client',
    initialSorting: [{ id: 'flagName', desc: false }],
    paginationMode: 'client',
    rowsPerPage: FLAG_GLOSSARY_PAGE_SIZE as RowsPerPageOption,
    initialPagination: {
      pageIndex: 0,
      pageSize: FLAG_GLOSSARY_PAGE_SIZE,
    },
    getRowId: (row) => row.entry.flagType,
  });

  // `autoResetAll` is off on the underlying table — reset when filters change
  // so operators are not left on an empty later page after the result set shrinks.
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentionally reset on filter change
  useEffect(() => {
    table.setPageIndex(0);
  }, [filters.severityTab, filters.categoryFilter, filters.search, table]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: clamp when filtered row count shrinks
  useEffect(() => {
    const maxIndex = Math.max(0, table.getPageCount() - 1);
    const { pageIndex } = table.getState().pagination;
    if (pageIndex > maxIndex) {
      table.setPageIndex(maxIndex);
    }
  }, [rows.length, table]);

  return {
    gridProps: {
      table,
      pageSize: pagination.pageSize as RowsPerPageOption,
      tableLoading: loading && rows.length === 0,
      gridQueryLoading: loading,
    },
  };
};

export { DEFAULT_TABLE_ROWS_PER_PAGE };
